import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats, getRecentBookings, getRecentOrders } from '../lib/api';
import { QuickBookingBar } from '../components/dashboard/QuickBookingBar';
import { 
  BedDouble, 
  Users, 
  Calendar, 
  DollarSign, 
  ArrowUpRight,
  Clock,
  LogIn,
  LogOut,
  UtensilsCrossed
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, ordersRes] = await Promise.all([
        getDashboardStats(),
        getRecentBookings(),
        getRecentOrders()
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data);
      setRecentOrders(ordersRes.data);
    } catch (error) {
      // silently handled — dashboard will show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Rooms',
      value: stats?.rooms?.total || 0,
      subtext: `${stats?.rooms?.available || 0} available`,
      icon: BedDouble,
      color: 'text-sage-700',
      bgColor: 'bg-sage-50',
    },
    {
      title: 'Occupancy Rate',
      value: `${stats?.rooms?.occupancy_rate || 0}%`,
      subtext: `${stats?.rooms?.occupied || 0} occupied`,
      icon: Calendar,
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
    },
    {
      title: 'Total Guests',
      value: stats?.guests?.total || 0,
      subtext: `${stats?.bookings?.active || 0} active bookings`,
      icon: Users,
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.revenue?.total || 0),
      subtext: 'All time',
      icon: DollarSign,
      color: 'text-stone-700',
      bgColor: 'bg-stone-100',
    },
  ];

  const quickStats = [
    { label: "Today's Check-ins", value: stats?.bookings?.todays_checkins || 0, icon: LogIn, color: 'text-emerald-600' },
    { label: "Today's Check-outs", value: stats?.bookings?.todays_checkouts || 0, icon: LogOut, color: 'text-sage-600' },
    { label: 'Pending Orders', value: stats?.orders?.pending || 0, icon: UtensilsCrossed, color: 'text-blue-600' },
  ];

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Dashboard</h1>
        <p className="text-stone-500 mt-1">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Quick Booking Bar */}
      <QuickBookingBar onBooked={loadData} />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, index) => (
          <div 
            key={card.title}
            className="stat-card animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`stat-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2 rounded-md ${card.bgColor}`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
            </div>
            <p className="text-2xl font-bold text-stone-900 font-['Manrope']">{card.value}</p>
            <p className="text-sm text-stone-500 mt-0.5">{card.title}</p>
            <p className="text-xs text-stone-400 mt-1">{card.subtext}</p>
          </div>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {quickStats.map((stat, index) => (
          <div 
            key={stat.label}
            className="bg-white border border-stone-200 rounded-md p-4 flex items-center gap-4 animate-fade-in"
            style={{ animationDelay: `${(index + 4) * 0.05}s` }}
          >
            <div className="p-2.5 bg-stone-50 rounded-md">
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">{stat.value}</p>
              <p className="text-sm text-stone-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bookings */}
        <div className="bg-white border border-stone-200 rounded-md">
          <div className="p-4 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900 font-['Manrope']">Recent Bookings</h2>
            <Link to="/bookings">
              <Button variant="ghost" size="sm" className="text-sage-700 hover:text-sage-800 hover:bg-sage-50 h-8 px-2">
                View all <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="divide-y divide-stone-100">
            {recentBookings.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                <p>No bookings yet</p>
              </div>
            ) : (
              recentBookings.map((booking, index) => (
                <div 
                  key={booking.id}
                  className="p-3 hover:bg-stone-50 animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`recent-booking-${booking.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-stone-900 truncate">{booking.guest?.name || 'Guest'}</p>
                      <p className="text-sm text-stone-500">
                        Room {booking.room?.room_number} • {booking.check_in_date} to {booking.check_out_date}
                      </p>
                    </div>
                    <span className={`status-badge status-${booking.status}`}>
                      {booking.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white border border-stone-200 rounded-md">
          <div className="p-4 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900 font-['Manrope']">Recent Orders</h2>
            <Link to="/orders">
              <Button variant="ghost" size="sm" className="text-sage-700 hover:text-sage-800 hover:bg-sage-50 h-8 px-2">
                View all <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="divide-y divide-stone-100">
            {recentOrders.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                <UtensilsCrossed className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                <p>No orders yet</p>
              </div>
            ) : (
              recentOrders.map((order, index) => (
                <div 
                  key={order.id}
                  className="p-3 hover:bg-stone-50 animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`recent-order-${order.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-stone-900">
                        {order.room_number ? `Room ${order.room_number}` : 'Walk-in'}
                      </p>
                      <p className="text-sm text-stone-500">
                        {order.items?.length || 0} items • {formatCurrency(order.total_amount)}
                      </p>
                    </div>
                    <span className={`status-badge status-${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
